/**
 * My Number — mobile-only talent phone-number storefront.
 *
 * Search by area code, buy a real US number, and it becomes the talent's
 * outbound caller ID in the dialer. The $5/mo fee lands on the talent's
 * billing ledger. Backed by /api/talent-phone/:talentId/*.
 */
import { useState } from "react";
import { useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useOwnedTalents } from "@/hooks/use-owned-talents";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  Loader2,
  Phone,
  Search,
  Check,
  ChevronLeft,
  MapPin,
  Trash2,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "@/components/ui/sheet";

interface StatusResponse {
  phoneNumber: string | null;
  callerIdLocked?: boolean;
  monthlyCents: number;
}
interface AvailableNumber {
  phoneNumber: string;
  friendlyName: string;
  locality: string | null;
  region: string | null;
}

const usd = (cents: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format((cents || 0) / 100);

/** +13125551234 → (312) 555-1234 */
function pretty(e164: string): string {
  const d = e164.replace(/\D/g, "");
  const ten = d.length === 11 && d[0] === "1" ? d.slice(1) : d;
  if (ten.length !== 10) return e164;
  return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
}

export default function MyNumber() {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const { ownedTalents, selectedTalentId, setSelectedTalentId, isLoading: talentLoading } =
    useOwnedTalents();

  const [areaCode, setAreaCode] = useState("");
  const [results, setResults] = useState<AvailableNumber[] | null>(null);
  const [confirm, setConfirm] = useState<AvailableNumber | null>(null);

  const { data: status, isLoading: statusLoading } = useQuery<StatusResponse>({
    queryKey: ["/api/talent-phone", selectedTalentId, "status"],
    queryFn: async () => {
      const r = await fetch(`/api/talent-phone/${encodeURIComponent(selectedTalentId!)}/status`, {
        credentials: "include",
      });
      if (!r.ok) throw new Error("failed to load");
      return r.json();
    },
    enabled: !!selectedTalentId,
  });

  const monthlyCents = status?.monthlyCents ?? 500;

  const search = useMutation({
    mutationFn: async () => {
      const r = await fetch(
        `/api/talent-phone/${encodeURIComponent(selectedTalentId!)}/available?areaCode=${encodeURIComponent(areaCode)}`,
        { credentials: "include" },
      );
      const json = await r.json();
      if (!r.ok) throw new Error(json?.message || "Search failed");
      return json.numbers as AvailableNumber[];
    },
    onSuccess: (numbers) => setResults(numbers),
    onError: (err: any) =>
      toast({ title: "Search failed", description: err?.message, variant: "destructive" }),
  });

  const purchase = useMutation({
    mutationFn: async (phoneNumber: string) => {
      const r = await apiRequest("POST", `/api/talent-phone/${selectedTalentId}/purchase`, { phoneNumber });
      return r.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/talent-phone", selectedTalentId, "status"] });
      setConfirm(null);
      setResults(null);
      setAreaCode("");
      toast({ title: "Number activated", description: "It's now your outbound caller ID." });
    },
    onError: (err: any) => {
      setConfirm(null);
      toast({ title: "Couldn't buy that number", description: err?.message, variant: "destructive" });
    },
  });

  const release = useMutation({
    mutationFn: async () => {
      await apiRequest("DELETE", `/api/talent-phone/${selectedTalentId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/talent-phone", selectedTalentId, "status"] });
      toast({ title: "Number released", description: "Billing for it has stopped." });
    },
    onError: (err: any) =>
      toast({ title: "Couldn't release", description: err?.message, variant: "destructive" }),
  });

  if (talentLoading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (ownedTalents.length === 0) {
    return (
      <div className="min-h-screen bg-background px-5 pt-10">
        <p className="text-sm text-muted-foreground">No talent profile is linked to your account yet.</p>
      </div>
    );
  }

  const locked = !!status?.callerIdLocked;

  return (
    <div className="min-h-screen bg-background" data-testid="backstage-my-number">
      <header className="px-5 pt-8 pb-3">
        <button onClick={() => navigate("/artist")} className="mb-3 flex items-center gap-1 text-sm text-muted-foreground" data-testid="button-back">
          <ChevronLeft className="h-4 w-4" /> Backstage
        </button>
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground/70">Backstage</p>
        <h1 className="mt-1 text-[26px] font-semibold tracking-tight text-foreground">My Number</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Your own line for calls and texts — {usd(monthlyCents)}/mo, billed to you.
        </p>

        {ownedTalents.length > 1 && (
          <div className="mt-3">
            <Select value={selectedTalentId ?? ""} onValueChange={(v) => { setSelectedTalentId(v); setResults(null); }}>
              <SelectTrigger className="h-9 w-full rounded-full border-border bg-card" data-testid="select-talent">
                <SelectValue placeholder="Select talent" />
              </SelectTrigger>
              <SelectContent>
                {ownedTalents.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </header>

      <div className="px-5">
        {statusLoading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : status?.phoneNumber ? (
          /* ── Active number card ──────────────────────────────── */
          <div className="rounded-3xl border border-border bg-card p-6 text-center" data-testid="card-active-number">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
              <Phone className="h-6 w-6 text-primary" />
            </div>
            <p className="mt-4 text-xs uppercase tracking-wide text-muted-foreground/70">Your number</p>
            <p className="mt-1 text-2xl font-semibold tracking-tight text-foreground tabular-nums" data-testid="text-active-number">
              {pretty(status.phoneNumber)}
            </p>
            <p className="mt-2 text-xs text-muted-foreground">Active caller ID · {usd(monthlyCents)}/mo</p>
            {!locked && (
              <button
                onClick={() => release.mutate()}
                disabled={release.isPending}
                className="mt-5 inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-4 py-2 text-sm font-medium text-destructive active:scale-[0.98] transition-transform disabled:opacity-60"
                data-testid="button-release-number"
              >
                {release.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                Release number
              </button>
            )}
            {locked && (
              <p className="mt-4 text-xs text-muted-foreground/80">Your caller ID is managed by your workspace admin.</p>
            )}
          </div>
        ) : locked ? (
          <div className="rounded-2xl border border-border bg-card p-6 text-center" data-testid="card-locked">
            <p className="text-sm text-muted-foreground">Your caller ID is managed by your workspace admin, so you can&apos;t buy a number here.</p>
          </div>
        ) : (
          /* ── Search + buy ────────────────────────────────────── */
          <div>
            <div className="rounded-2xl border border-border bg-card p-4">
              <p className="text-sm font-medium text-foreground">Pick your area code</p>
              <div className="mt-3 flex gap-2">
                <div className="relative flex-1">
                  <MapPin className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={areaCode}
                    onChange={(e) => setAreaCode(e.target.value.replace(/\D/g, "").slice(0, 3))}
                    inputMode="numeric"
                    placeholder="e.g. 312"
                    className="h-11 rounded-xl border-border bg-background pl-9 text-base tabular-nums"
                    data-testid="input-area-code"
                  />
                </div>
                <button
                  onClick={() => search.mutate()}
                  disabled={areaCode.length !== 3 || search.isPending}
                  className="flex h-11 items-center gap-1.5 rounded-xl bg-primary px-5 text-sm font-medium text-primary-foreground active:scale-[0.98] transition-transform disabled:opacity-50"
                  data-testid="button-search-numbers"
                >
                  {search.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                  Search
                </button>
              </div>
            </div>

            {results && (
              <div className="mt-5">
                <p className="mb-2 text-xs uppercase tracking-[0.18em] text-muted-foreground/70">
                  {results.length > 0 ? "Available" : "No numbers found"}
                </p>
                {results.length === 0 ? (
                  <p className="rounded-2xl border border-dashed border-border bg-card/50 p-6 text-center text-sm text-muted-foreground" data-testid="text-no-results">
                    Nothing open in {areaCode}. Try a nearby area code.
                  </p>
                ) : (
                  <div className="overflow-hidden rounded-2xl border border-border bg-card divide-y divide-border">
                    {results.map((n) => (
                      <button
                        key={n.phoneNumber}
                        onClick={() => setConfirm(n)}
                        className="flex w-full items-center justify-between gap-3 px-4 py-3.5 text-left active:bg-foreground/[0.03]"
                        data-testid={`row-number-${n.phoneNumber}`}
                      >
                        <div className="min-w-0">
                          <p className="text-base font-medium tabular-nums text-foreground">{pretty(n.phoneNumber)}</p>
                          <p className="truncate text-xs text-muted-foreground">
                            {[n.locality, n.region].filter(Boolean).join(", ") || "United States"}
                          </p>
                        </div>
                        <span className="shrink-0 rounded-full bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground">Get</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Confirm purchase ─────────────────────────────────────── */}
      <Sheet open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <SheetContent side="bottom" className="rounded-t-3xl" data-testid="sheet-confirm-purchase">
          <SheetHeader className="text-left">
            <SheetTitle>Get this number?</SheetTitle>
            <SheetDescription>
              {confirm && (
                <>
                  <span className="block text-lg font-semibold tabular-nums text-foreground">{pretty(confirm.phoneNumber)}</span>
                  <span className="block text-sm text-muted-foreground">
                    {[confirm.locality, confirm.region].filter(Boolean).join(", ") || "United States"}
                  </span>
                </>
              )}
            </SheetDescription>
          </SheetHeader>
          <div className="my-4 rounded-2xl border border-border bg-card p-4 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Monthly fee</span>
              <span className="font-semibold tabular-nums text-foreground">{usd(monthlyCents)}/mo</span>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              Billed to your talent account and becomes your outbound caller ID right away.
            </p>
          </div>
          <SheetFooter>
            <button
              onClick={() => confirm && purchase.mutate(confirm.phoneNumber)}
              disabled={purchase.isPending}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-3.5 text-sm font-semibold text-primary-foreground active:scale-[0.98] transition-transform disabled:opacity-60"
              data-testid="button-confirm-purchase"
            >
              {purchase.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              Confirm — {usd(monthlyCents)}/mo
            </button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <div className="h-10" />
    </div>
  );
}
